// apps/platform/src/components/BenefitExtraFieldEditor.tsx
//
// Inline list editor for activity_type_extra_fields. Used inside the
// ActivityTypeDrawer when the parent activity_type already exists (extra
// fields require an FK to a saved benefit_type_id).
//
// Each field row supports view → inline edit → save / cancel. New fields
// append via the "+ Adicionar campo" button. Deprecated fields render
// greyed-out and are not deletable (soft-delete preserves stored JSONB on
// existing activities — see plan §R21 / §AE6).

import { useState, type CSSProperties } from "react";
import {
  createExtraField,
  updateExtraField,
  deprecateExtraField,
  type BenefitTypeExtraField,
  type ExtraFieldInputType,
} from "../lib/benefitTypesClient";
import { ConfirmDialog } from "./ConfirmDialog";
import { colors, fontSize, fontWeight, radius, space } from "../lib/tokens";

const INPUT_TYPE_OPTIONS: Array<{ value: ExtraFieldInputType; label: string }> = [
  { value: "text",         label: "Texto curto" },
  { value: "textarea",     label: "Texto longo" },
  { value: "number",       label: "Número" },
  { value: "boolean",      label: "Sim/Não" },
  { value: "date",         label: "Data" },
  { value: "datetime",     label: "Data e hora" },
  { value: "dropdown",     label: "Dropdown" },
  { value: "multi_select", label: "Múltipla escolha" },
];

const KEY_RE = /^[a-z][a-z0-9_]*$/;

interface FieldFormState {
  key: string;
  admin_label: string;
  mobile_label: string;
  input_type: ExtraFieldInputType;
  required: boolean;
  visible_to_member: boolean;
  options_csv: string; // only used when input_type === "dropdown"
}

const EMPTY_FIELD: FieldFormState = {
  key: "",
  admin_label: "",
  mobile_label: "",
  input_type: "text",
  required: false,
  visible_to_member: true,
  options_csv: "",
};

function toFormState(f: BenefitTypeExtraField): FieldFormState {
  const cfg = f.config as { options?: string[] };
  return {
    key: f.key,
    admin_label: f.admin_label,
    mobile_label: f.mobile_label,
    input_type: f.input_type,
    required: f.required,
    visible_to_member: f.visible_to_member,
    options_csv: Array.isArray(cfg.options) ? cfg.options.join(", ") : "",
  };
}

function buildConfig(form: FieldFormState): Record<string, unknown> {
  if (form.input_type !== "dropdown") return {};
  const options = form.options_csv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return { options };
}

function validate(form: FieldFormState): string | null {
  if (!KEY_RE.test(form.key)) return "key deve casar com ^[a-z][a-z0-9_]*$";
  if (!form.admin_label.trim()) return "Rótulo admin obrigatório.";
  if (!form.mobile_label.trim()) return "Rótulo mobile obrigatório.";
  if (form.input_type === "dropdown") {
    const opts = form.options_csv.split(",").map((s) => s.trim()).filter(Boolean);
    if (opts.length === 0) return "Dropdown exige ao menos uma opção (separadas por vírgula).";
  }
  return null;
}

interface BenefitExtraFieldEditorProps {
  tenantId: string;
  benefitTypeId: string;
  fields: BenefitTypeExtraField[];
  onChanged: () => void;
}

export function BenefitExtraFieldEditor({ tenantId, benefitTypeId, fields, onChanged }: BenefitExtraFieldEditorProps) {
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<FieldFormState>(EMPTY_FIELD);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<BenefitTypeExtraField | null>(null);

  const openNew = () => {
    setEditingId("new");
    setForm(EMPTY_FIELD);
    setError(null);
  };

  const openEdit = (f: BenefitTypeExtraField) => {
    setEditingId(f.id);
    setForm(toFormState(f));
    setError(null);
  };

  const closeEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FIELD);
    setError(null);
  };

  const handleSave = async () => {
    const err = validate(form);
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);

    const payload = {
      key: form.key.trim(),
      admin_label: form.admin_label.trim(),
      mobile_label: form.mobile_label.trim(),
      input_type: form.input_type,
      required: form.required,
      visible_to_member: form.visible_to_member,
      config: buildConfig(form),
    };

    const res = editingId === "new"
      ? await createExtraField(tenantId, {
          benefit_type_id: benefitTypeId,
          sort_order: fields.length,
          ...payload,
        })
      : await updateExtraField(editingId!, payload);

    setSaving(false);
    if (res.error) { setError(res.error); return; }
    closeEdit();
    onChanged();
  };

  const handleDeprecateConfirm = async () => {
    if (!confirmTarget) return;
    setSaving(true);
    const res = await deprecateExtraField(confirmTarget.id);
    setSaving(false);
    setConfirmTarget(null);
    if (res.error) { setError(res.error); return; }
    onChanged();
  };

  return (
    <div>
      <div style={s.headerRow}>
        <h3 style={s.sectionTitle}>Campos extras</h3>
        {editingId === null && (
          <button type="button" onClick={openNew} style={s.addBtn} disabled={saving}>
            + Adicionar campo
          </button>
        )}
      </div>

      {fields.length === 0 && editingId !== "new" && (
        <div style={s.empty}>
          Nenhum campo extra. Este tipo usa apenas os campos universais (título, descrição, datas, mídia).
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: space.sm }}>
        {fields.map((f) => {
          const isEditing = editingId === f.id;
          const isDeprecated = !!f.deprecated_at;
          if (isEditing) {
            return (
              <FieldForm
                key={f.id}
                form={form}
                setForm={setForm}
                error={error}
                saving={saving}
                onSave={handleSave}
                onCancel={closeEdit}
              />
            );
          }
          return (
            <FieldRow
              key={f.id}
              field={f}
              isDeprecated={isDeprecated}
              onEdit={() => openEdit(f)}
              onDeprecate={() => setConfirmTarget(f)}
              disabled={editingId !== null || saving}
            />
          );
        })}

        {editingId === "new" && (
          <FieldForm
            form={form}
            setForm={setForm}
            error={error}
            saving={saving}
            onSave={handleSave}
            onCancel={closeEdit}
          />
        )}
      </div>

      <ConfirmDialog
        open={!!confirmTarget}
        title="Descontinuar campo?"
        message={
          <>
            <strong>{confirmTarget?.admin_label}</strong> não aparecerá mais ao criar novas atividades, mas atividades existentes continuam exibindo o valor armazenado em modo leitura.
          </>
        }
        confirmLabel="Descontinuar"
        variant="danger"
        loading={saving}
        onConfirm={handleDeprecateConfirm}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function FieldRow({
  field, isDeprecated, onEdit, onDeprecate, disabled,
}: {
  field: BenefitTypeExtraField;
  isDeprecated: boolean;
  onEdit: () => void;
  onDeprecate: () => void;
  disabled: boolean;
}) {
  const inputTypeLabel = INPUT_TYPE_OPTIONS.find((o) => o.value === field.input_type)?.label ?? field.input_type;
  return (
    <div style={isDeprecated ? { ...s.row, ...s.rowDeprecated } : s.row}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: space.sm, flexWrap: "wrap" }}>
          <span style={s.rowLabel}>{field.admin_label}</span>
          <span style={s.keyChip}>{field.key}</span>
          <span style={s.typeChip}>{inputTypeLabel}</span>
          {field.required && <span style={s.requiredBadge}>obrigatório</span>}
          {!field.visible_to_member && <span style={s.hiddenBadge}>oculto no app</span>}
          {isDeprecated && <span style={s.deprecatedBadge}>descontinuado</span>}
        </div>
        <div style={s.rowSub}>
          Rótulo mobile: {field.mobile_label}
          {field.input_type === "dropdown" && Array.isArray((field.config as { options?: string[] }).options) && (
            <> · Opções: {(field.config as { options: string[] }).options.join(", ")}</>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: space.xs, flexShrink: 0 }}>
        <button type="button" onClick={onEdit} disabled={disabled} style={s.rowBtn}>
          Editar
        </button>
        {!isDeprecated && (
          <button type="button" onClick={onDeprecate} disabled={disabled} style={s.rowBtnDanger}>
            Descontinuar
          </button>
        )}
      </div>
    </div>
  );
}

function FieldForm({
  form, setForm, error, saving, onSave, onCancel,
}: {
  form: FieldFormState;
  setForm: (next: FieldFormState | ((prev: FieldFormState) => FieldFormState)) => void;
  error: string | null;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const update = <K extends keyof FieldFormState>(key: K, value: FieldFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div style={s.formCard}>
      <div style={s.formGrid}>
        <label style={s.label}>
          <span style={s.labelText}>key <span style={s.required}>*</span></span>
          <input
            style={s.input}
            value={form.key}
            onChange={(e) => update("key", e.target.value.toLowerCase())}
            placeholder="ex: meditacao_horarios"
          />
        </label>
        <label style={s.label}>
          <span style={s.labelText}>Tipo</span>
          <select
            style={s.input}
            value={form.input_type}
            onChange={(e) => update("input_type", e.target.value as ExtraFieldInputType)}
          >
            {INPUT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
        <label style={s.label}>
          <span style={s.labelText}>Rótulo admin <span style={s.required}>*</span></span>
          <input
            style={s.input}
            value={form.admin_label}
            onChange={(e) => update("admin_label", e.target.value)}
            placeholder="Visto pelo staff no admin"
          />
        </label>
        <label style={s.label}>
          <span style={s.labelText}>Rótulo mobile <span style={s.required}>*</span></span>
          <input
            style={s.input}
            value={form.mobile_label}
            onChange={(e) => update("mobile_label", e.target.value)}
            placeholder="Visto pelo membro no app"
          />
        </label>
        {form.input_type === "dropdown" && (
          <label style={{ ...s.label, gridColumn: "1 / -1" }}>
            <span style={s.labelText}>Opções (separadas por vírgula) <span style={s.required}>*</span></span>
            <input
              style={s.input}
              value={form.options_csv}
              onChange={(e) => update("options_csv", e.target.value)}
              placeholder="ex: Café da manhã, Almoço, Jantar"
            />
          </label>
        )}
      </div>

      <div style={{ display: "flex", gap: space.lg, marginTop: space.sm }}>
        <label style={s.checkboxLabel}>
          <input
            type="checkbox"
            checked={form.required}
            onChange={(e) => update("required", e.target.checked)}
          />
          <span>Obrigatório</span>
        </label>
        <label style={s.checkboxLabel}>
          <input
            type="checkbox"
            checked={form.visible_to_member}
            onChange={(e) => update("visible_to_member", e.target.checked)}
          />
          <span>Visível no app do membro</span>
        </label>
      </div>

      {error && <div style={s.errorBox}>{error}</div>}

      <div style={s.formActions}>
        <button type="button" onClick={onCancel} disabled={saving} style={s.cancelBtn}>Cancelar</button>
        <button type="button" onClick={onSave} disabled={saving} style={s.submitBtn}>
          {saving ? "Salvando..." : "Salvar campo"}
        </button>
      </div>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const s: Record<string, CSSProperties> = {
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: space.md,
  },
  sectionTitle: {
    margin: 0,
    fontSize: fontSize.lg,
    fontWeight: fontWeight.semibold,
    color: colors.ink,
  },
  addBtn: {
    padding: `${space.xs}px ${space.md}px`,
    background: colors.indigoSoft,
    color: colors.indigo,
    border: `1px solid ${colors.indigo}`,
    borderRadius: radius.sm,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
  empty: {
    padding: space.lg,
    background: colors.paper,
    border: `1px dashed ${colors.fog}`,
    borderRadius: radius.sm,
    color: colors.stone,
    fontSize: fontSize.sm,
    textAlign: "center",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: space.md,
    padding: space.md,
    background: colors.white,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
  },
  rowDeprecated: {
    opacity: 0.6,
    background: colors.paper,
  },
  rowLabel: {
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    color: colors.ink,
  },
  rowSub: {
    fontSize: fontSize.sm,
    color: colors.stone,
    marginTop: 4,
  },
  keyChip: {
    fontFamily: "JetBrains Mono, monospace",
    fontSize: fontSize.xs,
    background: colors.paper,
    color: colors.stone,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.xs,
    padding: "1px 6px",
  },
  typeChip: {
    fontSize: fontSize.xs,
    background: colors.indigoSoft,
    color: colors.indigo,
    borderRadius: radius.xs,
    padding: "1px 6px",
  },
  requiredBadge: {
    fontSize: fontSize.xs,
    background: colors.warningSoft,
    color: colors.warning,
    borderRadius: radius.xs,
    padding: "1px 6px",
  },
  hiddenBadge: {
    fontSize: fontSize.xs,
    background: colors.fog,
    color: colors.stone,
    borderRadius: radius.xs,
    padding: "1px 6px",
  },
  deprecatedBadge: {
    fontSize: fontSize.xs,
    background: colors.errorSoft,
    color: colors.error,
    borderRadius: radius.xs,
    padding: "1px 6px",
  },
  rowBtn: {
    padding: `${space.xs}px ${space.md}px`,
    background: "transparent",
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    color: colors.ink,
    fontSize: fontSize.sm,
    cursor: "pointer",
  },
  rowBtnDanger: {
    padding: `${space.xs}px ${space.md}px`,
    background: "transparent",
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    color: colors.error,
    fontSize: fontSize.sm,
    cursor: "pointer",
  },
  formCard: {
    padding: space.md,
    background: colors.paper,
    border: `1px solid ${colors.indigo}`,
    borderRadius: radius.sm,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: space.md,
  },
  label: {
    display: "flex",
    flexDirection: "column",
    gap: space.xs,
    fontSize: fontSize.sm,
  },
  labelText: {
    color: colors.stone,
    fontWeight: fontWeight.medium,
  },
  required: { color: colors.error },
  input: {
    padding: space.sm,
    fontSize: fontSize.base,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    background: colors.white,
    color: colors.ink,
    fontFamily: "inherit",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: space.xs,
    fontSize: fontSize.sm,
    color: colors.stone,
    cursor: "pointer",
  },
  errorBox: {
    color: colors.error,
    fontSize: fontSize.sm,
    marginTop: space.sm,
    background: colors.errorSoft,
    padding: space.sm,
    borderRadius: radius.sm,
  },
  formActions: {
    display: "flex",
    gap: space.sm,
    justifyContent: "flex-end",
    marginTop: space.md,
  },
  cancelBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    border: `1px solid ${colors.fog}`,
    background: "transparent",
    borderRadius: radius.sm,
    color: colors.ink,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
  submitBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    background: colors.indigo,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
};
